import { store } from '@/store'
import Api from '@/providers/Api'
import router from '@/router/router'
import * as _ from 'lodash'

/**
 * Service to manage user authentication/authorization state against Yii API.
 */

export default {

    // Check if the current user auth_key is valid - if it is not, as a side effect the user will be logged out.
    // This called when the app is loaded.
    checkAuth () {
        const path = router.currentRoute.path
        const name = router.currentRoute.name
        const routesNoAuthorization = [
            'twofa', 'resetConfirm', 'userEmailActivate',
            'sitePrivacy', 'siteAbout', 'siteFAQ'
        ]
        if ((path !== '/user/login') && !routesNoAuthorization.includes(name)) {
            // interceptor will logout if auth is not valid (see Api.checkAuth)
            return Api.checkAuth()
        }
    },

    // returns either the user record or an empty associative array {}
    setUserFromLoginResponse (vue, response) {
        const data = response.body
        if (data.auth_key) {

            this.commitCurrentUser(data)
            this.commitMaskingStack([])

            Api.log(vue, 'user', null, 'login', `User logged in: ${data.email}`)
            return data
        } else {
            this.commitCurrentUser({ name: 'Spoof' })
            return {}
        }
    },

    loginUser (vue, email, password) {
        return Api.loginUser(vue, email, password)
            .then((response) => {
                let data = response.body
                if (!data.twofa_enabled) {
                    return this.setUserFromLoginResponse(vue, response)
                } else {
                    this.commitCurrentUser({
                        pending_id: data.id,
                        twofa_token: data.twofa_token
                    })
                    return data
                }
            })
    },

    loginTwofa (vue, code) {
        let twofa_token = store.state.currentUser.twofa_token
        return Api.loginTwofa(vue, twofa_token, code)
            .then((response) => {
                if (response && response.body && response.body.email) {
                    return this.setUserFromLoginResponse(vue, response)
                } else {
                    return {}
                }
            })
    },

    logoutUser (vue) {
        return Api.logoutUser(vue)
            .then(() => {
                // NB: must log before yii2User is unset; don't log logouts generated by checkauth
                if (store.state.currentUser.email) {
                    Api.log(vue, 'user', null, 'logout',
                        `User logged out: ${store.state.currentUser.email}`)
                }

                this.commitCurrentUser({})
                this.commitMaskingStack([])

                store.dispatch('submit', {
                    mutation: 'clearHelps'
                })
                store.dispatch('submit', {
                    mutation: 'clearIntros'
                })
                store.dispatch('submit', {
                    mutation: 'clearFilters'
                })
                store.dispatch("contexts", {
                  mutation: "contexts"
                });

                if (router.currentRoute.path !== '/') {
                    router.replace('/')
                }
            })
    },

    maskAsUser (userId) {
        return Api.maskAsUser(userId)
            .then((response) => {
                if (response.body.auth_key) {
                    store.state.maskingUserStack.push(store.state.currentUser) // remember who we were
                    this.commitMaskingStack(store.state.maskingUserStack)
                    this.commitCurrentUser(response.body)

                    router.push('/')
                } else {
                    alert('Masking error')
                }
            })
    },

    unmaskUser () {
        // NB: no need for a server-side unmask action in Yii 2, as we just pop the user here...
        this.commitCurrentUser(store.state.maskingUserStack.pop())

        // "mask" as the original user, just to refresh their auth_key
        // (it may have expired during the masking session)
        return Api.maskAsUser(store.state.currentUser.id)
            .then((response) => {
                // NB: leave store.state.yii2MaskingUserStack as is, as we aren't doing a real mask
                this.commitCurrentUser(response.body)
                router.push('/')
            })
    },

    async refresh () {
        try {
            const response = await Api.refreshUser()
            this.commitCurrentUser(response.body)
        } catch (e) {
            console.log('User.refresh error - user not logged in')
        }
    },

    activate (key, userModel, agencyUserId) {
        return Api.activate(key, userModel, agencyUserId)
    },
    resetPassword (vue, email, password) {
        return Api.resetPassword(vue, email, password)
    },
    confirmPasswordReset (vue, email, confirm) {
        return Api.confirmPasswordReset(vue, email, confirm)
    },
    loadEnvironment () {
        return Api.loadEnvironment()
            .then((response) => {
                store.dispatch('submit', {
                    mutation: 'environment',
                    payload: response.body
                })
            })
    },

    // load the current user state from local storage
    //TODO: investigate why this isn't getting called
    loadUserState () {
        console.log('[loadUserState]')
        let stack = store.state.maskingUserStack
        if (!_.isArray(stack)) {
            stack = []
        }
        store.dispatch('submit', {
            mutation: 'maskingUserStack',
            payload: stack
        })
        console.log('[user]', store.state.currentUser)
        if (!store.state.currentUser.name) {
            let vuex = JSON.parse(localStorage.getItem('vuex'))
            console.log('[vuex]', vuex)
            if (vuex && vuex.currentUser) {
                store.dispatch('submit', {
                    mutation: 'currentUser',
                    payload: vuex.currentUser
                })
            }
        }
    },

    // save the current user state to local storage
    // persistUserState () {
    //     localStorage.setItem('maskingUserStack', JSON.stringify(store.state.maskingUserStack))
    //     localStorage.setItem('currentUser', JSON.stringify(store.state.currentUser))
    // },

    commitCurrentUser (currentUser) {
        store.dispatch('submit', {
            mutation: 'currentUser',
            payload: currentUser
        })
    },

    commitMaskingStack (maskingUserStack) {
        store.dispatch('submit', {
            mutation: 'maskingUserStack',
            payload: maskingUserStack
        })
    },

    noLoggedInUser () {
        return store.state.currentUser.role === undefined
    },

    userId () {
        return store.state.currentUser.id
    },

    isMasking () {
        return store.state.maskingUserStack.length > 0
    },

    email () {
        return store.state.currentUser.email
    },

    isSuperAdmin () {
        // added "!!" to force a boolean value
        return !!store.state.currentUser.is_superadmin
    },

    isAdmin () {
        return !!store.state.currentUser.is_admin
    },

    isAuditor () {
        return !!store.state.currentUser.is_auditor
    },

    isOnlyAuditor () {
        return this.isAuditor() && !(store.state.currentUser.agencyUsers?.length || this.isEquipmentOwner() || this.isAdmin() || this.isSuperAdmin())
    },

    isDisabled () {
        return !!store.state.currentUser.disabled
    },

    isProjectManager () {
        if (!store.state.currentUser.agencyUsers) {
            return false
        }
        for (let agencyUser of store.state.currentUser.agencyUsers) {
            if (agencyUser.is_project_manager) {
                return true
            }
        }
        return false
    },

    isOwnersRepresentative () {
        if (!store.state.currentUser.agencyUsers) {
            return false
        }
        for (let agencyUser of store.state.currentUser.agencyUsers) {
            if (agencyUser.is_owners_representative) {
                return true
            }
        }
        return false
    },

    isProgramAdmin () {
        if (!store.state.currentUser.agencyUsers) {
            return false
        }
        for (let agencyUser of store.state.currentUser.agencyUsers) {
            if (agencyUser.is_program_admin) {
                return true
            }
        }
        return false
    },

    isEquipmentOwner () {
        return store.state.currentUser.companyUsers && store.state.currentUser.companyUsers.length > 0
    },

    belongsToCompany (companyId) {
        if (!store.state.currentUser.companyUsers || !companyId) {
            return false
        }
        let belongs = false
        store.state.currentUser.companyUsers.forEach((c) => {
            if (c.company_id === companyId) {
                belongs = true
            }
        })
        return belongs
    },

    isCompanyAdmin (companyId) {
        if (!store.state.currentUser.companyUsers || !companyId) {
            return false
        }
        return store.state.currentUser.companyUsers.reduce((company, acc) => {
            return company.company_id === companyId && company.is_company_admin ? true : acc
        }, false).length > 0
    },

    getCompanyId () {
        let companies = store.state.currentUser.companyUsers
        if (companies && companies[0]) {
            return companies[0].company_id
        }
        return null
    },

    getCompanyName () {
        let companies = store.state.currentUser.companyUsers
        if (companies && companies[0]) {
            return companies[0].companyName
        }
        return null
    },

    getAgencyName () {
        let agencies = store.state.currentUser.agencyUsers
        if (agencies && agencies[0]) {
            return agencies[0].agencyName
        }
        return null
    },

    getAgencyId () {
        let agencies = store.state.currentUser.agencyUsers
        if (agencies && agencies[0]) {
            return agencies[0].agency_id
        }
        return null
    },

    danglingCompanyUser () {
        const companyUsers = store.state.currentUser.danglingCompanyUsers
        return companyUsers ? companyUsers[0] : null
    },

    agencyUserNeedingSetup () {
        const agencyUser = store.state.currentUser.agencyUsersNeedingSetup
        return agencyUser ? agencyUser[0] : null
    },

    needsCompanyUserSetup () {
        //Only show setup if all company user relations are 'dangling' i.e. unapproved
        //so that adding a second companyUser relation doesn't get them stuck on the setup page
        let user = store.state.currentUser
        return user.danglingCompanyUsers && user.companyUsers &&
            user.danglingCompanyUsers.length > 0 &&
            user.danglingCompanyUsers.length === user.companyUsers.length
    }
}
